import type { Element } from './element';
import { MnaMatrix } from './mna';
import { Port, SubcktInstance, type SubcktDef } from './subckt';
import './catalog'; // 소자 생성기 등록 (모듈 안쪽 소자를 만들 때 필요)

export interface SolveResult {
  ok: boolean;
  error?: string;
  iterations: number;
}

interface FlowEdge {
  el: Element;
  a: number; // 점 인덱스
  b: number;
}

interface FlowGroup {
  /** BFS 순서의 점 인덱스 */
  order: number[];
  /** 각 점의 부모 간선 (root 는 -1) */
  parentEdge: Int32Array;
  parentVertex: Int32Array;
}

const KEY = (x: number, y: number) => `${x},${y}`;

/** 시뮬레이션에 실제로 들어가는 소자 + 각 핀의 점 키 (모듈 안쪽은 접두어가 붙는다) */
interface SimEntry {
  el: Element;
  keys: string[];
}

const MAX_DEPTH = 8;

/**
 * 회로 전체. 소자 목록을 들고 넷리스트를 만들어 매 타임스텝 MNA 로 푼다.
 *
 * 설계 노트
 * - 도선/닫힌 스위치/전류계처럼 저항 0인 소자는 행렬에 넣지 않고
 *   union-find 로 노드를 합쳐버린다 (수치 조건수를 망치지 않으려고).
 * - 합쳐진 노드 안에서의 전류는 해를 푼 뒤 신장트리 흐름 계산으로 복원한다.
 */
export class Circuit {
  /** 화면에 놓인 최상위 소자들 (편집 대상) */
  elements: Element[] = [];
  /** 이 회로가 쓰는 모듈 정의들 (id → 정의) */
  defs = new Map<string, SubcktDef>();
  time = 0;
  lastResult: SolveResult = { ok: true, iterations: 0 };
  /** 빌드 중 발견한 경고 (없는 모듈 정의 등) */
  warnings: string[] = [];
  /** 주변 온도 (℃) */
  ambient = 25;
  /** 적응형 스텝 상태 / 통계 */
  dtNext = 1e-5;
  dtMax = 0.05;
  stats = { steps: 0, rejected: 0, lastDt: 0, behind: false };

  /** 모듈을 펼친 뒤 실제로 푸는 소자 목록 */
  private sim: SimEntry[] = [];
  private simElements: Element[] = [];
  private needsBuild = true;
  private mna: MnaMatrix | null = null;
  private nodeCount = 0;

  /** 격자점 키 -> 점 인덱스 */
  private pointIndex = new Map<string, number>();
  private pointKeys: string[] = [];
  /** 점 인덱스 -> 노드 인덱스 (-1 = 접지) */
  private pointNode: Int32Array = new Int32Array(0);
  private hasNonlinear = false;

  private flowEdges: FlowEdge[] = [];
  private flowGroups: FlowGroup[] = [];
  private injection: Float64Array = new Float64Array(0);
  private elPoints: number[][] = [];
  private groundPoints: number[] = [];
  private subtotal: Float64Array = new Float64Array(0);

  markDirty(): void {
    this.needsBuild = true;
  }

  add(el: Element): void {
    this.elements.push(el);
    this.markDirty();
  }

  remove(el: Element): void {
    const i = this.elements.indexOf(el);
    if (i >= 0) this.elements.splice(i, 1);
    this.markDirty();
  }

  clear(): void {
    this.elements = [];
    this.defs.clear();
    this.time = 0;
    this.markDirty();
  }

  /** 모듈 정의 추가/갱신 — 같은 id 를 쓰는 인스턴스들의 포트 배치도 맞춰준다 */
  upsertDef(def: SubcktDef): void {
    this.defs.set(def.id, def);
    const visit = (list: Element[]) => {
      for (const el of list) {
        if (!(el instanceof SubcktInstance)) continue;
        if (el.defId === def.id) el.syncWithDef(def);
        visit(el.inner);
      }
    };
    visit(this.elements);
    this.markDirty();
  }

  /** 이 회로(모듈 안쪽 포함)에서 실제로 쓰는 모듈 정의 id 들 */
  usedDefIds(): Set<string> {
    const used = new Set<string>();
    const visitDef = (id: string) => {
      if (used.has(id)) return;
      used.add(id);
      const def = this.defs.get(id);
      if (!def) return;
      for (const e of def.elements) if (e.type === 'subckt') visitDef(String(e.props.defId ?? ''));
    };
    for (const el of this.elements) if (el instanceof SubcktInstance) visitDef(el.defId);
    return used;
  }

  reset(): void {
    this.time = 0;
    this.dtNext = 1e-5;
    for (const el of this.elements) {
      el.resetState();
      el.current = 0;
      el.voltageDrop = 0;
    }
    this.build();
    for (const el of this.simElements) {
      el.resetThermal(this.ambient);
      el.errRatio = 0;
    }
    for (const el of this.elements) el.resetThermal(this.ambient);
  }

  /** 모듈 안쪽까지 펼친, 실제로 계산되는 소자들 */
  get allElements(): Element[] {
    if (this.needsBuild) this.build();
    return this.simElements;
  }

  /* ───────────────────── 넷리스트 빌드 ───────────────────── */

  /** 모듈 인스턴스를 재귀적으로 펼쳐 시뮬레이션 목록을 만든다 */
  private flatten(): SimEntry[] {
    const out: SimEntry[] = [];
    this.warnings = [];
    const walk = (list: Element[], prefix: string, depth: number, portMap: Map<string, string> | null) => {
      for (const el of list) {
        if (el instanceof SubcktInstance) {
          const def = this.defs.get(el.defId);
          if (!def) {
            this.warnings.push(`"${el.label}" 모듈 정의를 찾을 수 없어요`);
            continue;
          }
          if (depth >= MAX_DEPTH) {
            this.warnings.push(`"${el.label}" 모듈이 너무 깊게(자기 자신을) 포함하고 있어요`);
            continue;
          }
          el.ensureInner(def);
          const pinKeys = el.pins.map((p) => prefix + KEY(p.x, p.y));
          const map = new Map<string, string>();
          el.portOrder.forEach((name, i) => map.set(name, pinKeys[i]));
          walk(el.inner, `${prefix}${el.id}/`, depth + 1, map);
          continue;
        }
        const keys = el.pins.map((p) => prefix + KEY(p.x, p.y));
        if (el instanceof Port && portMap) {
          const outer = portMap.get(el.portName);
          if (outer) {
            portMap.delete(el.portName); // 같은 이름 포트는 첫 번째만 연결
            keys.push(outer);
          }
        }
        out.push({ el, keys });
      }
    };
    walk(this.elements, '', 0, null);
    return out;
  }

  build(): void {
    this.pointIndex.clear();
    this.pointKeys = [];
    this.sim = this.flatten();
    this.simElements = this.sim.map((e) => e.el);

    const pointOfKey = (k: string): number => {
      let i = this.pointIndex.get(k);
      if (i === undefined) {
        i = this.pointKeys.length;
        this.pointKeys.push(k);
        this.pointIndex.set(k, i);
      }
      return i;
    };

    // 1. 모든 핀의 점 수집
    const elPoints: number[][] = this.sim.map((e) => e.keys.map(pointOfKey));

    const np = this.pointKeys.length;
    const parent = new Int32Array(np);
    for (let i = 0; i < np; i++) parent[i] = i;
    const find = (i: number): number => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]];
        i = parent[i];
      }
      return i;
    };
    const union = (a: number, b: number): void => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[rb] = ra;
    };

    // 2. 이상 도체(도선·닫힌 스위치·전류계·모듈 포트)는 노드를 합친다
    this.flowEdges = [];
    this.sim.forEach(({ el }, i) => {
      if (el.idealConductor && elPoints[i].length === 2) {
        union(elPoints[i][0], elPoints[i][1]);
        this.flowEdges.push({ el, a: elPoints[i][0], b: elPoints[i][1] });
      }
    });

    // 3. 접지 루트 결정
    const groundRoots = new Set<number>();
    this.groundPoints = [];
    this.sim.forEach(({ el }, i) => {
      if (el.isGround && elPoints[i].length > 0) {
        groundRoots.add(find(elPoints[i][0]));
        this.groundPoints.push(elPoints[i][0]);
      }
    });
    const implicit = this.simElements.some((el) => el.usesImplicitGround);
    if (groundRoots.size === 0 && np > 0 && !implicit) {
      // 접지를 안 놓았으면 첫 소자의 첫 핀을 기준점으로 삼는다
      groundRoots.add(find(0));
    }

    // 4. 노드 번호 부여
    const rootNode = new Map<number, number>();
    let n = 0;
    for (let i = 0; i < np; i++) {
      const r = find(i);
      if (rootNode.has(r)) continue;
      rootNode.set(r, groundRoots.has(r) ? -1 : n++);
    }
    this.nodeCount = n;

    this.pointNode = new Int32Array(np);
    for (let i = 0; i < np; i++) this.pointNode[i] = rootNode.get(find(i)) ?? -1;

    // 5. 소자에 노드/가지 인덱스 부여
    let vs = 0;
    this.hasNonlinear = false;
    this.elPoints = elPoints;
    this.sim.forEach(({ el }, i) => {
      el.nodes = elPoints[i].map((p) => this.pointNode[p]);
      el.vsIndex = -1;
      if (!el.idealConductor && el.voltageSourceCount > 0) {
        el.vsIndex = vs;
        vs += el.voltageSourceCount;
      }
      if (el.nonlinear) this.hasNonlinear = true;
    });

    this.mna = new MnaMatrix(n, vs);
    this.injection = new Float64Array(np);
    this.subtotal = new Float64Array(np);
    this.buildFlowGroups(np);
    this.needsBuild = false;
    this.dtNext = Math.min(this.dtNext, 1e-4); // 회로가 바뀌면 작은 스텝부터 다시
  }

  /** 합쳐진 노드 내부 전류 복원을 위한 신장트리 준비 */
  private buildFlowGroups(np: number): void {
    this.flowGroups = [];
    if (this.flowEdges.length === 0) return;

    const adj: { edge: number; to: number }[][] = Array.from({ length: np }, () => []);
    this.flowEdges.forEach((e, i) => {
      adj[e.a].push({ edge: i, to: e.b });
      adj[e.b].push({ edge: i, to: e.a });
    });

    // 접지 소자가 붙은 점을 우선 루트로 (불균형 전류를 흡수)
    const preferRoot = new Set<number>(this.groundPoints);

    const visited = new Uint8Array(np);
    const startOrder = [...preferRoot, ...Array.from({ length: np }, (_, i) => i)];

    for (const start of startOrder) {
      if (visited[start] || adj[start].length === 0) continue;
      const order: number[] = [start];
      const parentEdge = new Int32Array(np).fill(-1);
      const parentVertex = new Int32Array(np).fill(-1);
      visited[start] = 1;
      for (let qi = 0; qi < order.length; qi++) {
        const v = order[qi];
        for (const { edge, to } of adj[v]) {
          if (visited[to]) continue;
          visited[to] = 1;
          parentEdge[to] = edge;
          parentVertex[to] = v;
          order.push(to);
        }
      }
      this.flowGroups.push({ order, parentEdge, parentVertex });
    }
  }

  /* ───────────────────── 시뮬레이션 ───────────────────── */

  /**
   * 적응형 시간 전진: span 초만큼 시뮬레이션한다.
   * 매 스텝 커패시터·인덕터가 오차를 보고하고, 너무 크면 상태를 되돌려 반으로 줄여 다시 푼다.
   * 오차가 작으면 스텝을 키운다. maxSteps 를 다 쓰면 실시간보다 뒤처진 것(behind).
   */
  advance(span: number, maxSteps = 400): SolveResult {
    let remaining = span;
    let steps = 0;
    let rejected = 0;
    const els = this.allElements;
    let hint = this.dtMax;
    for (const el of els) hint = Math.min(hint, el.maxStep);
    while (remaining > 1e-12 && steps < maxSteps) {
      if (this.needsBuild) this.build();
      const dt = Math.max(1e-9, Math.min(this.dtNext, remaining, hint));
      const snaps = this.simElements.map((el) => el.snapshot());
      const t0 = this.time;
      const r = this.step(dt, false);
      steps++;
      let err = 0;
      for (const el of this.simElements) if (el.errRatio > err) err = el.errRatio;
      if ((!r.ok || err > 1) && dt > 2e-9) {
        // 거절: 되돌리고 스텝 줄이기
        this.simElements.forEach((el, i) => el.restore(snaps[i]));
        this.time = t0;
        this.dtNext = Math.max(1e-9, dt * (r.ok ? Math.max(0.2, Math.min(0.5, 0.9 / Math.sqrt(err))) : 0.25));
        rejected++;
        continue;
      }
      for (const el of this.simElements) el.afterAccept(dt, this.ambient);
      remaining -= dt;
      this.stats.lastDt = dt;
      const grow = err > 1e-8 ? Math.min(2, Math.max(1, 0.9 / Math.sqrt(err))) : 2;
      this.dtNext = Math.min(this.dtMax, dt * grow);
    }
    this.stats = { steps, rejected, lastDt: this.stats.lastDt, behind: remaining > 1e-9 };
    return this.lastResult;
  }

  step(dt: number, commit = true): SolveResult {
    if (this.needsBuild) this.build();
    const m = this.mna;
    const els = this.simElements;
    if (!m || els.length === 0) {
      this.lastResult = { ok: true, iterations: 0 };
      return this.lastResult;
    }

    for (const el of els) el.beginStep(dt, this.time);

    const maxIter = this.hasNonlinear ? 200 : 1;
    let iterations = 0;
    let converged = !this.hasNonlinear;

    for (let it = 0; it < maxIter; it++) {
      m.clear();
      for (const el of els) {
        if (el.idealConductor || el.isGround) continue;
        el.stamp(m);
      }
      if (!m.solve()) {
        this.lastResult = { ok: false, error: '회로를 풀 수 없어요 (전압원 단락이나 끊긴 연결을 확인해 보세요)', iterations: it };
        return this.lastResult;
      }
      iterations = it + 1;
      if (!this.hasNonlinear) break;

      let maxDelta = 0;
      for (const el of els) {
        if (!el.nonlinear) continue;
        maxDelta = Math.max(maxDelta, el.updateNewton(m));
      }
      if (maxDelta < 1e-9) {
        converged = true;
        break;
      }
    }

    for (const el of els) el.endStep(m, dt);
    this.resolveIdealCurrents();
    if (commit) for (const el of els) el.afterAccept(dt, this.ambient);

    this.time += dt;
    this.lastResult = converged
      ? { ok: true, iterations }
      : { ok: false, error: '비선형 해석이 수렴하지 않았어요 (시간 간격을 줄여보세요)', iterations };
    return this.lastResult;
  }

  /** 도선/전류계 등 합쳐진 구간의 전류를 신장트리로 복원 */
  private resolveIdealCurrents(): void {
    if (this.flowEdges.length === 0) return;
    this.injection.fill(0);

    this.sim.forEach(({ el }, idx) => {
      if (el.idealConductor || el.isGround) return;
      const pts = this.elPoints[idx];
      const into = el.pinCurrents();
      for (let i = 0; i < pts.length; i++) {
        // 소자에서 노드로 흘러 나오는 전류 = -(소자로 들어가는 전류)
        this.injection[pts[i]] -= into[i] ?? 0;
      }
    });

    for (const e of this.flowEdges) e.el.current = 0;
    this.subtotal.set(this.injection);

    for (const g of this.flowGroups) {
      for (let i = g.order.length - 1; i >= 1; i--) {
        const v = g.order[i];
        const ei = g.parentEdge[v];
        if (ei < 0) continue;
        const flow = this.subtotal[v]; // v 에서 부모 쪽으로 빠져나가는 전류
        const edge = this.flowEdges[ei];
        edge.el.current = v === edge.a ? flow : -flow;
        this.subtotal[g.parentVertex[v]] += flow;
        this.subtotal[v] = 0;
      }
    }
  }

  /* ───────────────────── 조회 ───────────────────── */

  voltageAt(x: number, y: number): number {
    if (!this.mna) return 0;
    const p = this.pointIndex.get(KEY(x, y));
    if (p === undefined) return 0;
    return this.mna.nodeVoltage(this.pointNode[p]);
  }

  hasPoint(x: number, y: number): boolean {
    return this.pointIndex.has(KEY(x, y));
  }

  /** 그 격자점에 연결된 핀 개수 (3개 이상이면 접합점 표시) */
  pinCountAt(x: number, y: number): number {
    let c = 0;
    for (const el of this.elements) {
      for (const p of el.pins) if (p.x === x && p.y === y) c++;
    }
    return c;
  }

  get nodeTotal(): number {
    return this.nodeCount;
  }

  maxAbsVoltage(): number {
    if (!this.mna) return 1;
    let max = 1e-6;
    for (let i = 0; i < this.nodeCount; i++) max = Math.max(max, Math.abs(this.mna.x[i]));
    return max;
  }
}
